import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { contactsApi, entitiesApi, projectsApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Mail, Phone, Building2, Pencil, Send, Route, ExternalLink, Plus, UserPlus, Shield, Clock } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import ContactDialog from "@/components/ContactDialog";
import EmailDialog from "@/components/EmailDialog";
import SendToCompassButton from "@/components/compass/SendToCompassButton";
import TeamInviteDialog from "@/components/TeamInviteDialog";

/**
 * Merge a Contact's direct entity links with any derived_relationships
 * surfaced through linked Organizations. Each entity appears ONCE:
 * if it exists both directly and via an org, the direct row is
 * primary and secondary "via <org>" provenance is listed beneath.
 */
function buildEntityCards(contact, derived, entityById) {
  const cards = new Map();               // entity_id → card
  for (const eid of contact.entity_ids || []) {
    cards.set(eid, {
      entity_id: eid,
      entity_name: entityById[eid]?.name || eid.slice(0, 6),
      primary: "direct",
      via: [],
    });
  }
  for (const d of derived || []) {
    if (!d?.entity_id) continue;
    const existing = cards.get(d.entity_id);
    if (existing) {
      // Direct already exists — list org as secondary provenance.
      existing.via.push({
        organization_id: d.via_organization_id,
        organization_name: d.via_organization_name,
        project_id: d.project_id,
        project_name: d.project_name,
        added_at: d.added_at,
        relationship_status: d.relationship_status,
      });
    } else {
      cards.set(d.entity_id, {
        entity_id: d.entity_id,
        entity_name: d.entity_name || entityById[d.entity_id]?.name
          || d.entity_id.slice(0, 6),
        primary: "via_organization",
        via_organization_id: d.via_organization_id,
        via_organization_name: d.via_organization_name,
        project_id: d.project_id,
        project_name: d.project_name,
        added_at: d.added_at,
        relationship_status: d.relationship_status,
        via: [],
      });
    }
  }
  return Array.from(cards.values());
}

function fmtDate(s) {
  if (!s) return "";
  try {
    return new Date(s).toLocaleDateString(undefined,
      { year: "numeric", month: "short", day: "numeric" });
  } catch { return s; }
}

export default function ContactDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [entities, setEntities] = useState([]);
  const [projects, setProjects] = useState([]);
  const [edit, setEdit] = useState(false);
  const [emailDlg, setEmailDlg] = useState(false);
  const [addProjOpen, setAddProjOpen] = useState(false);
  const [newProjId, setNewProjId] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);

  const load = async () => {
    const [c, e, p] = await Promise.all([
      contactsApi.get(id), entitiesApi.list(), projectsApi.list(),
    ]);
    setData(c.data); setEntities(e.data); setProjects(p.data || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const addProjectLink = async () => {
    if (!newProjId) return toast.error("Pick a project");
    try {
      await contactsApi.addProjectLink(id, newProjId);
      toast.success("Linked to project");
      setAddProjOpen(false); setNewProjId("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Link failed"); }
  };
  const removeProjectLink = async (pid) => {
    if (!window.confirm("Remove this project link?")) return;
    try {
      await contactsApi.removeProjectLink(id, pid);
      toast.success("Project link removed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Remove failed"); }
  };

  const entityById = useMemo(
    () => Object.fromEntries(entities.map(e => [e.id, e])),
    [entities]);
  const cards = useMemo(
    () => data ? buildEntityCards(data.contact, data.derived_relationships, entityById) : [],
    [data, entityById]);

  if (!data) return <div className="p-8 text-white/50">Loading…</div>;
  const c = data.contact;
  const linkedUser = data.linked_user;
  const pendingInvite = data.pending_invite;

  return (
    <div className="h-full overflow-y-auto p-8" data-testid="contact-detail">
      <Link to="/contacts" className="inline-flex items-center gap-1.5 text-xs text-white/50 hover:text-white transition-colors mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to contacts
      </Link>
      <div className="flex items-start justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="font-display text-4xl font-semibold tracking-tight">{c.full_name}</h1>
            {linkedUser && (
              <Link to={`/member/${linkedUser.id}`}
                    className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider px-2 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/15"
                    data-testid="contact-team-member-badge">
                <Shield className="w-3 h-3" /> Team Member
              </Link>
            )}
            {!linkedUser && pendingInvite && (
              <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider px-2 py-1 rounded-full border border-amber-500/30 bg-amber-500/10 text-amber-300"
                    data-testid="contact-invite-pending-badge">
                <Clock className="w-3 h-3" /> Invite pending
              </span>
            )}
          </div>
          <div className="text-white/60 mt-1">{c.role}{c.company ? ` @ ${c.company}` : ""}</div>
          <div className="mt-4 space-y-1.5">
            {c.email && <div className="text-sm text-white/70 flex items-center gap-2"><Mail className="w-3.5 h-3.5"/>{c.email}</div>}
            {c.phone && <div className="text-sm text-white/70 flex items-center gap-2"><Phone className="w-3.5 h-3.5"/>{c.phone}</div>}
            {c.company && <div className="text-sm text-white/70 flex items-center gap-2"><Building2 className="w-3.5 h-3.5"/>{c.company}</div>}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {c.email && <Button onClick={() => setEmailDlg(true)} className="rounded-full bg-white text-black hover:bg-white/90" data-testid="send-email-btn"><Send className="w-4 h-4 mr-2"/>Email</Button>}
          <SendToCompassButton targetType="contact" targetId={c.id}
                               testid="contact-send-to-compass" />
          {!linkedUser && !pendingInvite && (
            <Button variant="outline" onClick={() => setInviteOpen(true)}
                    className="rounded-full border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/10"
                    data-testid="contact-add-to-team-btn">
              <UserPlus className="w-4 h-4 mr-1.5" /> Add to Team
            </Button>
          )}
          {linkedUser && (
            <Link to={`/member/${linkedUser.id}`}>
              <Button variant="outline" className="rounded-full"
                      data-testid="contact-open-member-profile-btn">
                <Shield className="w-4 h-4 mr-1.5" /> Open member profile
              </Button>
            </Link>
          )}
          <Button variant="outline" onClick={() => setAddProjOpen(true)}
                  className="rounded-full"
                  data-testid="contact-add-to-project-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Add to Project
          </Button>
          <Button variant="ghost" onClick={() => setEdit(true)} className="text-white/70 hover:text-white hover:bg-white/10"><Pencil className="w-4 h-4 mr-2"/>Edit</Button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="p-5 rounded-xl border border-white/10 bg-[#151A23] md:col-span-2"
             data-testid="contact-linked-entities">
          <div className="text-eyebrow mb-3">Linked Entities</div>
          {cards.length === 0 ? (
            <div className="text-sm text-white/40">Not linked to any entity yet.</div>
          ) : (
            <div className="space-y-2">
              {cards.map(card => (
                <div key={card.entity_id}
                     className="p-3 rounded-lg border border-white/10 bg-white/[0.02]"
                     data-testid={`contact-entity-card-${card.entity_id}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <Link to={`/entity/${card.entity_id}`}
                          className="text-white font-medium hover:underline">
                      {card.entity_name}
                    </Link>
                    {card.primary === "direct" ? (
                      <span className="text-[11px] px-2 py-0.5 rounded-md border border-emerald-500/25 bg-emerald-500/15 text-emerald-300"
                            data-testid={`contact-provenance-direct-${card.entity_id}`}>
                        Direct
                      </span>
                    ) : (
                      <span className="text-[11px] px-2 py-0.5 rounded-md border border-white/15 bg-white/5 text-white/70 inline-flex items-center gap-1"
                            data-testid={`contact-provenance-via-${card.entity_id}`}>
                        <Route className="w-3 h-3" />
                        via {card.via_organization_name}
                      </span>
                    )}
                  </div>
                  {card.primary === "via_organization" && (
                    <div className="text-[11px] text-white/50 mt-1.5 flex flex-wrap gap-2">
                      <Link to={`/organizations/${card.via_organization_id}`}
                            className="hover:text-white/85 inline-flex items-center gap-1">
                        {card.via_organization_name} <ExternalLink className="w-3 h-3" />
                      </Link>
                      {card.project_name && <span>· Project: {card.project_name}</span>}
                      {card.added_at && <span>· {fmtDate(card.added_at)}</span>}
                      {card.relationship_status && <span>· {card.relationship_status}</span>}
                    </div>
                  )}
                  {card.primary === "direct" && card.via.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5 text-[11px] text-white/50">
                      {card.via.map((v, i) => (
                        <span key={i}
                              className="px-2 py-0.5 rounded-md border border-white/10 bg-white/[0.03] inline-flex items-center gap-1">
                          also via <Link to={`/organizations/${v.organization_id}`}
                                          className="hover:text-white">{v.organization_name}</Link>
                          {v.project_name ? ` · ${v.project_name}` : ""}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-5 rounded-xl border border-white/10 bg-[#151A23]">
          <div className="text-eyebrow mb-3">Notes</div>
          <div className="text-sm text-white/70 whitespace-pre-wrap">{c.notes || "No notes."}</div>
        </div>

        <div className="p-5 rounded-xl border border-white/10 bg-[#151A23]">
          <div className="text-eyebrow mb-3">Assigned Tasks ({data.tasks.length})</div>
          {data.tasks.length === 0 && <div className="text-sm text-white/40">None.</div>}
          <div className="space-y-2">
            {data.tasks.map(t => (
              <Link key={t.id} to={`/task/${t.id}`}
                    className="text-sm flex items-center justify-between p-2.5 rounded border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition"
                    data-testid={`contact-task-${t.id}`}>
                <div>
                  <div className={t.status === "Done" ? "line-through text-white/40" : ""}>{t.name}</div>
                  <div className="text-xs text-white/50">{t.status} · {t.priority}{t.due_date ? ` · due ${t.due_date}` : ""}</div>
                </div>
                <span className="text-xs text-white/50">Open →</span>
              </Link>
            ))}
          </div>
        </div>

        <div className="p-5 rounded-xl border border-white/10 bg-[#151A23] md:col-span-2">
          <div className="text-eyebrow mb-3">Email History ({data.emails.length})</div>
          {data.emails.length === 0 && <div className="text-sm text-white/40">No emails sent yet.</div>}
          <div className="space-y-2">
            {data.emails.map(m => (
              <div key={m.id} className="p-3 rounded border border-white/10 bg-white/[0.03]">
                <div className="flex items-center justify-between mb-1">
                  <div className="font-medium text-sm">{m.subject}</div>
                  <div className="text-xs text-white/40">{new Date(m.sent_at).toLocaleString()}</div>
                </div>
                <div className="text-xs text-white/50 line-clamp-2" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(m.html_content || "") }}/>
              </div>
            ))}
          </div>
        </div>
      </div>

      <ContactDialog open={edit} setOpen={setEdit} contact={c} onSaved={() => { setEdit(false); load(); }}/>
      <EmailDialog open={emailDlg} setOpen={setEmailDlg} contact={c} onSent={() => { setEmailDlg(false); load(); }}/>
      <TeamInviteDialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        preselectContact={c}
        onSent={() => load()} />

      <Dialog open={addProjOpen} onOpenChange={(v) => !v && setAddProjOpen(false)}>
        <DialogContent className="max-w-md" data-testid="contact-add-project-dialog">
          <DialogHeader>
            <DialogTitle>Add to Project</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-xs text-white/50">
              Link {c.full_name} to another project. Existing links are not affected.
            </div>
            <Select value={newProjId} onValueChange={setNewProjId}>
              <SelectTrigger data-testid="contact-add-project-select"><SelectValue placeholder="Pick a project" /></SelectTrigger>
              <SelectContent>
                {projects
                  .filter(p => !(c.project_ids || []).includes(p.id))
                  .map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)
                }
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddProjOpen(false)}>Cancel</Button>
            <Button onClick={addProjectLink} className="bg-white text-black hover:bg-white/90" data-testid="contact-add-project-confirm">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
